import React from "react";
import logoZ from "../../assets/images/logos/logoZ.png";
import logoZASFIT from "../../assets/images/logos/logoZASFIT.png";
import "./PrivacyPolicyPage.css";
import { Helmet } from "react-helmet-async";

export default function PrivacyPolicyPage() {
  const SUPPORT_EMAIL =
    process.env.REACT_APP_SUPPORT_EMAIL || "support@zasfit.io";

  const APP_NAME =
    process.env.REACT_APP_PUBLIC_APP_NAME || "Zasfit";

  const LEGAL_ENTITY =
    process.env.REACT_APP_LEGAL_ENTITY || "Pablo Díaz Amores";

  const LAST_UPDATED =
    process.env.REACT_APP_PRIVACY_LAST_UPDATED || "7 de junio de 2026";

  return (
    <>
      <Helmet>
        <title>Política de Privacidad | {APP_NAME}</title>
        <meta
          name="description"
          content={`Política de Privacidad de ${APP_NAME}. Información sobre recopilación, uso, conservación y eliminación de datos personales por parte de ${LEGAL_ENTITY}.`}
        />
        <meta name="robots" content="index,follow" />
      </Helmet>

      <main className="privacy-page">
        <section className="privacy-hero">
          <div className="privacy-hero-glow privacy-hero-glow-one" />
          <div className="privacy-hero-glow privacy-hero-glow-two" />

          <div className="privacy-container">
            <header className="privacy-header">
              <div className="privacy-brand-card">
                <img
                  src={logoZ}
                  alt={`${APP_NAME} icon`}
                  className="privacy-logo-icon"
                />
                <img
                  src={logoZASFIT}
                  alt={APP_NAME}
                  className="privacy-wordmark"
                />
              </div>

              <div className="privacy-eyebrow">Legal</div>

              <h1>Política de Privacidad</h1>

              <p className="privacy-lead">
                Esta Política de Privacidad corresponde a la aplicación{" "}
                <strong>{APP_NAME}</strong>, publicada en Google Play por{" "}
                <strong>{LEGAL_ENTITY}</strong>. {APP_NAME} es una plataforma
                SaaS que permite a entrenadores gestionar clientes, rutinas,
                entrenamientos, hábitos, dietas, seguimiento de progreso y otros
                servicios relacionados con la actividad física y el bienestar.
                Esta Política explica cómo {LEGAL_ENTITY}, como entidad legal
                responsable, recopila, utiliza, conserva, protege y elimina los
                datos personales tratados a través de {APP_NAME} y de las
                aplicaciones móviles personalizadas o white label desplegadas
                mediante esta plataforma.
              </p>

              <div className="privacy-meta">
                <span>Última actualización: {LAST_UPDATED}</span>
                <span>Aplicación: {APP_NAME}</span>
                <span>Responsable legal: {LEGAL_ENTITY}</span>
              </div>
            </header>

            <section className="privacy-summary-grid" aria-label="Resumen">
              <div className="privacy-summary-card">
                <span className="privacy-summary-label">Responsable legal</span>
                <strong>{LEGAL_ENTITY}</strong>
              </div>

              <div className="privacy-summary-card">
                <span className="privacy-summary-label">Contacto</span>
                <strong>{SUPPORT_EMAIL}</strong>
              </div>

              <div className="privacy-summary-card">
                <span className="privacy-summary-label">Aplicación</span>
                <strong>{APP_NAME}</strong>
              </div>
            </section>

            <article className="privacy-prose">
              <section>
                <h2>1. Responsable y ámbito de aplicación</h2>

                <p>
                  Esta Política de Privacidad se aplica a{" "}
                  <strong>{APP_NAME}</strong>, así como a las aplicaciones
                  móviles personalizadas o white label para entrenadores que
                  sean creadas, desplegadas, gestionadas o proporcionadas
                  mediante la plataforma {APP_NAME}.
                </p>

                <p>
                  La entidad legal responsable de la publicación de la aplicación
                  en Google Play y de la prestación tecnológica de la plataforma
                  es <strong>{LEGAL_ENTITY}</strong>, desarrollador indicado en
                  la ficha de Google Play.
                </p>

                <p>
                  {APP_NAME} actúa como plataforma SaaS que permite a
                  entrenadores gestionar clientes, entrenamientos, rutinas,
                  seguimiento, hábitos, dietas, progreso físico y otra
                  información relacionada con la actividad física y el bienestar.
                  Cada entrenador puede disponer de una aplicación personalizada
                  o white label para prestar sus servicios a sus propios
                  clientes.
                </p>

                <p>
                  A través de {APP_NAME} y de las aplicaciones móviles
                  desplegadas mediante esta plataforma, {LEGAL_ENTITY} puede
                  recopilar, utilizar, conservar, proteger y eliminar datos
                  personales necesarios para el funcionamiento del servicio, la
                  gestión técnica de las aplicaciones, el soporte, la seguridad,
                  la mejora de la plataforma y el cumplimiento de obligaciones
                  legales.
                </p>
              </section>

              <section>
                <h2>2. Información que recopilamos</h2>

                <p>
                  Podemos recopilar información que el usuario proporciona
                  directamente al crear o utilizar su cuenta, incluyendo nombre,
                  correo electrónico, identificador de usuario, credenciales de
                  acceso y cualquier información introducida voluntariamente en
                  la plataforma.
                </p>

                <p>
                  También podemos tratar información relacionada con el uso de la
                  aplicación, progreso, rutinas asignadas, hábitos, registros de
                  entrenamiento, datos de seguimiento, preferencias, dietas,
                  información de bienestar y otra información necesaria para
                  prestar el servicio.
                </p>

                <p>
                  Además, podemos recopilar datos técnicos y de diagnóstico, como
                  información del dispositivo, rendimiento, errores, registros de
                  actividad, versión de la aplicación e información necesaria
                  para mantener la seguridad, estabilidad y correcto
                  funcionamiento del servicio.
                </p>
              </section>

              <section>
                <h2>3. Datos de entrenamiento, salud y bienestar</h2>

                <p>
                  La aplicación puede mostrar o permitir el registro de
                  información relacionada con entrenamiento, hábitos, nutrición,
                  composición corporal, progreso físico u otros datos vinculados
                  al bienestar. Esta información se utiliza para que el usuario y
                  su entrenador puedan realizar seguimiento del proceso de
                  entrenamiento.
                </p>

                <p>
                  {APP_NAME} no presta servicios médicos, no realiza diagnósticos
                  y no sustituye la evaluación de un profesional sanitario.
                  Cualquier información de salud o bienestar se trata únicamente
                  para prestar las funcionalidades de seguimiento, organización y
                  comunicación ofrecidas por la plataforma.
                </p>
              </section>

              <section>
                <h2>4. Cómo utilizamos la información</h2>

                <p>Utilizamos los datos personales para las siguientes finalidades:</p>

                <ul>
                  <li>Crear, autenticar y gestionar cuentas de usuario.</li>
                  <li>Permitir el acceso a la aplicación y a sus funcionalidades.</li>
                  <li>
                    Mostrar rutinas, planes, hábitos, dietas, recomendaciones o
                    contenidos asignados por un entrenador.
                  </li>
                  <li>
                    Permitir el seguimiento del progreso, registros y actividad
                    del usuario.
                  </li>
                  <li>
                    Facilitar la comunicación y gestión entre entrenadores y sus
                    clientes.
                  </li>
                  <li>Prestar soporte técnico y responder consultas.</li>
                  <li>
                    Mejorar la seguridad, rendimiento, estabilidad y
                    funcionalidad del servicio.
                  </li>
                  <li>
                    Cumplir obligaciones legales, prevenir usos indebidos y
                    proteger nuestros derechos.
                  </li>
                </ul>
              </section>

              <section>
                <h2>5. Compartición de información</h2>

                <p>
                  No vendemos ni alquilamos información personal a terceros.
                  Podemos compartir datos únicamente cuando sea necesario para
                  prestar el servicio, operar la infraestructura técnica, analizar
                  errores, ofrecer soporte, mantener la seguridad o cumplir
                  obligaciones legales.
                </p>

                <p>
                  Podemos utilizar proveedores de servicios tecnológicos, como
                  alojamiento, autenticación, almacenamiento, analítica técnica,
                  notificaciones, monitorización de errores o herramientas de
                  soporte. Estos proveedores solo pueden tratar los datos de
                  acuerdo con nuestras instrucciones y con las medidas de
                  seguridad aplicables.
                </p>

                <p>
                  Cuando la cuenta de un usuario está asociada a un entrenador,
                  el entrenador puede acceder a la información necesaria para
                  gestionar el seguimiento del usuario, asignar rutinas, revisar
                  progreso, gestionar hábitos, dietas o contenidos y prestar su
                  servicio de entrenamiento.
                </p>

                <p>
                  En el caso de aplicaciones white label desplegadas mediante{" "}
                  {APP_NAME}, el entrenador o marca correspondiente puede actuar
                  como prestador directo del servicio de entrenamiento frente a
                  sus clientes, mientras que {LEGAL_ENTITY} proporciona la
                  infraestructura tecnológica necesaria para el funcionamiento de
                  la aplicación y la plataforma.
                </p>
              </section>

              <section>
                <h2>6. Conservación de los datos</h2>

                <p>
                  Conservamos los datos personales mientras la cuenta del usuario
                  permanezca activa o mientras sea necesario para prestar el
                  servicio, mantener la seguridad de la plataforma, resolver
                  incidencias, cumplir obligaciones legales o atender posibles
                  responsabilidades.
                </p>

                <p>
                  Cuando los datos dejan de ser necesarios para las finalidades
                  indicadas, los eliminamos, anonimizamos o bloqueamos conforme a
                  los plazos legalmente aplicables y a nuestras obligaciones
                  técnicas, contractuales o regulatorias.
                </p>

                <p>
                  Los datos técnicos, registros de seguridad, logs de actividad,
                  datos de diagnóstico y copias de seguridad pueden conservarse
                  durante un periodo limitado adicional para prevenir fraude,
                  investigar incidencias, mantener la seguridad y garantizar la
                  continuidad del servicio.
                </p>

                <p>
                  Las copias de seguridad, registros técnicos, logs de seguridad
                  y datos de diagnóstico pueden conservarse durante un periodo
                  adicional limitado, normalmente hasta 90 días, antes de ser
                  eliminados, sobrescritos o anonimizados, salvo que sea
                  necesario conservarlos durante más tiempo para cumplir
                  obligaciones legales, investigar incidencias de seguridad o
                  resolver disputas.
                </p>
              </section>

              <section>
                <h2>7. Eliminación de datos y cuenta</h2>

                <p>
                  Los usuarios pueden solicitar la eliminación de su cuenta y de
                  sus datos personales escribiendo a{" "}
                  <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                </p>

                <p>
                  La solicitud debe incluir el correo electrónico asociado a la
                  cuenta para que podamos identificar al usuario y verificar la
                  petición. Una vez recibida una solicitud válida, eliminaremos o
                  anonimizaremos los datos personales asociados a la cuenta,
                  salvo aquellos que debamos conservar por obligación legal,
                  motivos de seguridad, prevención de fraude, resolución de
                  disputas o defensa de reclamaciones.
                </p>

                <p>
                  Si la cuenta está vinculada a un entrenador, también puede ser
                  necesario coordinar la eliminación o desvinculación con el
                  entrenador o entidad responsable de la relación de
                  entrenamiento.
                </p>

                <p>
                  Las solicitudes de eliminación se procesarán normalmente en un
                  plazo máximo de 30 días desde la verificación de la identidad
                  del solicitante, salvo que sea necesario conservar determinada
                  información durante más tiempo por obligación legal, prevención
                  de fraude, seguridad o defensa de reclamaciones.
                </p>
              </section>

              <section>
                <h2>8. Seguridad</h2>

                <p>
                  Aplicamos medidas técnicas y organizativas razonables para
                  proteger los datos personales frente a accesos no autorizados,
                  pérdida, alteración, divulgación o destrucción indebida.
                </p>

                <p>
                  Ningún sistema es completamente seguro. Por ello, recomendamos
                  a los usuarios mantener la confidencialidad de sus
                  credenciales, utilizar contraseñas seguras y contactar con
                  nosotros si detectan cualquier uso no autorizado de su cuenta.
                </p>
              </section>

              <section>
                <h2>9. Menores de edad</h2>

                <p>
                  La plataforma no está dirigida a menores que no cuenten con la
                  edad mínima legal aplicable o con la autorización necesaria de
                  sus padres, tutores o representantes legales. Si detectamos que
                  hemos recopilado datos de un menor sin la autorización
                  requerida, adoptaremos medidas razonables para eliminar dicha
                  información.
                </p>
              </section>

              <section>
                <h2>10. Transferencias y proveedores internacionales</h2>

                <p>
                  Algunos proveedores tecnológicos pueden estar ubicados fuera
                  del país de residencia del usuario. En esos casos, adoptamos
                  medidas razonables para que el tratamiento de datos cuente con
                  garantías adecuadas conforme a la normativa aplicable.
                </p>
              </section>

              <section>
                <h2>11. Cambios en esta Política</h2>

                <p>
                  Podemos actualizar esta Política de Privacidad para reflejar
                  cambios legales, técnicos o funcionales. Cuando publiquemos una
                  nueva versión, actualizaremos la fecha indicada al inicio de
                  esta página. El uso continuado de la aplicación tras la
                  publicación de cambios implica el conocimiento de la versión
                  actualizada.
                </p>
              </section>

              <section>
                <h2>12. Contacto</h2>

                <p>
                  El responsable legal y desarrollador indicado en Google Play es{" "}
                  <strong>{LEGAL_ENTITY}</strong>.
                </p>

                <p>
                  La aplicación <strong>{APP_NAME}</strong> y las aplicaciones
                  móviles personalizadas o white label desplegadas mediante esta
                  plataforma son gestionadas tecnológicamente por{" "}
                  <strong>{LEGAL_ENTITY}</strong>.
                </p>

                <p>
                  Para cualquier pregunta sobre esta Política de Privacidad, el
                  tratamiento de datos personales, la conservación de información
                  o la eliminación de una cuenta, puedes escribirnos a{" "}
                  <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                </p>
              </section>
            </article>
          </div>
        </section>
      </main>
    </>
  );
}